'use strict';

/**
 * The ONE password policy for the ParcelPuffin ecosystem — NIST SP 800-63B-4 §3.1.1.2 (owner ruling
 * 2026-09-26, superseding R8-12's "10+, classes, blocklist"). Adopted from UNREACHABLE-MODULE-
 * BURNDOWN-001 lane L16's design; the export surface is the one both lanes agreed (L16 handoff §8,
 * plus L-R7CLOSE's three changes: the context argument is REQUIRED, the blocklist and context
 * compare the NFKC + lower-cased value, and the 72-byte ceiling lives here only).
 *
 * - a LENGTH FLOOR of 15, counted in Unicode code points of the RAW string (what bcrypt hashes);
 * - a CEILING of 72 UTF-8 bytes — bcrypt reads no more, so a longer password would be silently
 *   truncated; it is refused instead, with the reason;
 * - a BLOCKLIST of common, expected and compromised values and their trivial derivatives
 *   (`./commonPasswords`), matched on the NORMALISED value, so a full-width spelling of a listed
 *   root is the same guess (Q-R8CLOSE-14 (a));
 * - a CONTEXT check: the service's own name and the person's names, email and caller words do not
 *   count toward the floor (normalised the same way);
 * - and, deliberately, NO composition rule ("verifiers SHALL NOT impose other composition rules").
 *
 * ONE table drives both halves: `evaluatePasswordRequirements` (the checklist a person sees while
 * typing) and `validatePassword` (the gate every server applies before hashing) read the same rows,
 * so what is shown and what is refused cannot disagree. `passwordRefusal` is the ONE server refusal;
 * `passwordPolicyFailures` is the client's reader of it.
 *
 * @module @puffin-core/password-policy/passwordPolicy
 */

const { isCommonPassword } = require('./commonPasswords');

/** The policy's numbers. Change one here and every server gate and every checklist moves. */
const PASSWORD_POLICY = Object.freeze({ minLength: 15, maxBytes: 72 });

/** The requirement keys — every client catalog translates these; a refusal lists them. */
const PASSWORD_RULE = Object.freeze({
  MIN_LENGTH: 'minLength',
  MAX_BYTES: 'maxBytes',
  NOT_COMMON: 'notCommon',
  NOT_PERSONAL: 'notPersonal',
});

/** The error code every server answers a policy refusal with (HTTP 400). */
const PASSWORD_POLICY_ERROR_CODE = 'PASSWORD_POLICY_VIOLATION';

/** Words that never count toward the floor, whoever is choosing: the service's own name. */
const SERVICE_WORDS = Object.freeze(['puffin']);

/** A context word shorter than this is too likely to occur by chance to be meaningful. */
const MIN_CONTEXT_WORD_LENGTH = 3;

/**
 * The server-side English for each requirement — the `message` of a refusal, and logs. A screen
 * never shows these; it translates the KEY.
 */
const PASSWORD_REQUIREMENT_MESSAGES = Object.freeze({
  [PASSWORD_RULE.MIN_LENGTH]: `use at least ${PASSWORD_POLICY.minLength} characters`,
  [PASSWORD_RULE.MAX_BYTES]: `use no more than ${PASSWORD_POLICY.maxBytes} bytes`,
  [PASSWORD_RULE.NOT_COMMON]: 'avoid common or easily guessed passwords',
  [PASSWORD_RULE.NOT_PERSONAL]: 'do not build it from your name, your email or the word Puffin',
});

/**
 * Length in Unicode code points — `'é'` is one and an emoji outside the BMP is one, where
 * `String#length` would count two.
 *
 * @param {string} password
 * @returns {number}
 */
function passwordLength(password) {
  return [...password].length;
}

/**
 * The UTF-8 byte length bcrypt will see, computed from code points so the answer is identical in
 * Node, every browser and Hermes with no `TextEncoder`. A lone surrogate encodes as U+FFFD, three
 * bytes, exactly as Node's `Buffer` does.
 *
 * @param {string} password
 * @returns {number}
 */
function passwordByteLength(password) {
  let bytes = 0;
  for (const char of password) {
    const codePoint = char.codePointAt(0);
    if (codePoint < 0x80) bytes += 1;
    else if (codePoint < 0x800) bytes += 2;
    else if (codePoint < 0x10000) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

/**
 * The form the blocklist and the context compare: NFKC folds compatibility spellings (full-width
 * letters, ligatures) onto the characters an attacker's list holds, then lower-case. Length and
 * bytes are NEVER measured on this form — they measure what is hashed.
 *
 * @param {string} value
 * @returns {string}
 */
function normaliseForMatch(value) {
  return value.normalize('NFKC').toLowerCase();
}

/** Separators between the pieces of a name, a company or an email local part. */
const WORD_SEPARATOR = /[^a-z0-9\u0080-\uffff]+/;

/**
 * A value and the ways a person folds it into a password: the whole, each piece, and the pieces
 * run together (`harbor print` → `harbor print`, `harbor`, `print`, `harborprint`).
 *
 * @param {string} value normalised
 * @returns {string[]}
 */
function foldings(value) {
  const pieces = value.split(WORD_SEPARATOR).filter(Boolean);
  return [value, ...pieces, pieces.join('')];
}

/**
 * The normalised context words for one person: the service words; the email, its local part and
 * that part's foldings, and the domain's first label; the first and last name and any caller words,
 * each with its foldings. Deduplicated, each at least three characters.
 *
 * @param {PasswordContext} context
 * @returns {string[]}
 */
function contextWords(context) {
  const words = [...SERVICE_WORDS];
  const email = typeof context.email === 'string' ? normaliseForMatch(context.email.trim()) : '';
  if (email) {
    const [local, domain = ''] = email.split('@');
    words.push(email, ...foldings(local), domain.split('.')[0]);
  }
  for (const value of [context.firstName, context.lastName, ...(context.words || [])]) {
    if (typeof value === 'string') words.push(...foldings(normaliseForMatch(value.trim())));
  }
  return [...new Set(words.filter((word) => word.length >= MIN_CONTEXT_WORD_LENGTH))];
}

/**
 * Does the password lean on a context word? It does when a word occurs in it AND removing every
 * context word leaves fewer than the floor — `my-blue-puffin-sings-loud` passes (nineteen
 * characters without the service word) and `ParcelPuffin2026!` does not.
 *
 * @param {string} normalised the password, normalised
 * @param {string[]} words
 * @returns {boolean}
 */
function leansOnContext(normalised, words) {
  const found = words.filter((word) => normalised.includes(word));
  if (found.length === 0) return false;
  // Longest first, so `parcelpost` is removed whole rather than leaving `post` behind.
  const remainder = found
    .sort((a, b) => b.length - a.length)
    .reduce((rest, word) => rest.split(word).join(''), normalised);
  return passwordLength(remainder) < PASSWORD_POLICY.minLength;
}

/**
 * The policy as a TABLE — one row per requirement, in display order. `shown: 'always'` rows belong
 * on every checklist; `shown: 'whenUnmet'` rows appear only when they fail (a "no more than 72
 * bytes" line on every form would be noise for a limit almost nobody reaches).
 */
const REQUIREMENTS = Object.freeze([
  {
    id: PASSWORD_RULE.MIN_LENGTH,
    shown: 'always',
    met: ({ raw }) => passwordLength(raw) >= PASSWORD_POLICY.minLength,
  },
  {
    id: PASSWORD_RULE.MAX_BYTES,
    shown: 'whenUnmet',
    met: ({ raw }) => passwordByteLength(raw) <= PASSWORD_POLICY.maxBytes,
  },
  {
    id: PASSWORD_RULE.NOT_COMMON,
    shown: 'always',
    // An EMPTY password is not "not common" — nothing has been checked, and a checklist that
    // ticked this row before the person typed would be asserting something false.
    met: ({ raw, normalised }) => raw.length > 0 && !isCommonPassword(normalised),
  },
  {
    id: PASSWORD_RULE.NOT_PERSONAL,
    shown: 'always',
    met: ({ raw, normalised, words }) => raw.length > 0 && !leansOnContext(normalised, words),
  },
]);

/** Every requirement key, in display order. */
const PASSWORD_RULE_KEYS = Object.freeze(REQUIREMENTS.map((row) => row.id));

/**
 * @typedef {Object} PasswordContext
 * @property {string} [email]      the person's email
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string[]} [words]    other words specific to this person or company (name, subdomain)
 */

/**
 * The context is REQUIRED: a caller that forgot it would silently disarm `notPersonal` for every
 * person it serves. `{}` is the explicit "I know nothing about them".
 *
 * @param {string} fn the public function's name, for the message
 * @param {unknown} context
 * @returns {void}
 */
function requireContext(fn, context) {
  if (context === null || typeof context !== 'object' || Array.isArray(context)) {
    throw new TypeError(
      `${fn}: a context object is required — pass the person’s { email, firstName, lastName, ` +
        'words }, or {} when the caller knows nothing about them',
    );
  }
}

/**
 * Evaluate every requirement.
 *
 * @param {unknown} password a non-string is treated as empty
 * @param {PasswordContext} context
 * @returns {Array<{id: string, met: boolean, shown: string}>}
 */
function evaluateAll(password, context) {
  const raw = typeof password === 'string' ? password : '';
  const subject = { raw, normalised: normaliseForMatch(raw), words: contextWords(context) };
  return REQUIREMENTS.map((row) => ({ id: row.id, met: row.met(subject), shown: row.shown }));
}

/**
 * The rows a live checklist renders, in display order: every `always` row, plus any `whenUnmet` row
 * that currently fails. Each row carries the policy numbers so a label can interpolate them.
 *
 * @param {unknown} password
 * @param {PasswordContext} context - REQUIRED; `{}` when nothing is known about the person
 * @returns {Array<{id: string, met: boolean, minLength: number, maxBytes: number}>}
 */
function evaluatePasswordRequirements(password, context) {
  requireContext('evaluatePasswordRequirements', context);
  return evaluateAll(password, context)
    .filter((row) => row.shown === 'always' || !row.met)
    .map((row) => ({ id: row.id, met: row.met, ...PASSWORD_POLICY }));
}

/**
 * The gate every server applies before hashing a password a person chose, and every client before
 * submitting.
 *
 * @param {unknown} password
 * @param {PasswordContext} context - REQUIRED; `{}` when nothing is known about the person
 * @returns {{valid: boolean, failed: string[]}} `failed` lists requirement keys in display order
 */
function validatePassword(password, context) {
  requireContext('validatePassword', context);
  const failed = evaluateAll(password, context)
    .filter((row) => !row.met)
    .map((row) => row.id);
  return { valid: failed.length === 0, failed };
}

/**
 * The ONE server refusal — judge and build in one call. Hand the fields to the app's response
 * helper: `sendBadRequest(res, refusal.message, { code: refusal.code, details: refusal.details })`.
 *
 * @param {unknown} password
 * @param {PasswordContext} context - REQUIRED; `{}` when nothing is known about the person
 * @returns {null | {code: string, message: string,
 *   details: {failed: string[], minLength: number, maxBytes: number}}} `null` when accepted
 */
function passwordRefusal(password, context) {
  requireContext('passwordRefusal', context);
  const { valid, failed } = validatePassword(password, context);
  if (valid) return null;
  const reasons = failed.map((id) => PASSWORD_REQUIREMENT_MESSAGES[id]);
  return {
    code: PASSWORD_POLICY_ERROR_CODE,
    message: `Choose a stronger password: ${reasons.join('; ')}.`,
    details: { failed, ...PASSWORD_POLICY },
  };
}

/**
 * Is this API error a password-policy refusal? Reads the `{code, details}` envelope every client
 * transport surfaces; returns the failed keys this client knows, or `null` for any other error.
 *
 * @param {{code?: string, details?: {failed?: unknown}}|null|undefined} error
 * @returns {string[]|null}
 */
function passwordPolicyFailures(error) {
  if (!error || error.code !== PASSWORD_POLICY_ERROR_CODE) return null;
  const failed = error.details && error.details.failed;
  if (!Array.isArray(failed)) return null;
  return failed.filter((id) => PASSWORD_RULE_KEYS.includes(id));
}

module.exports = {
  PASSWORD_POLICY,
  PASSWORD_RULE,
  PASSWORD_RULE_KEYS,
  PASSWORD_POLICY_ERROR_CODE,
  PASSWORD_REQUIREMENT_MESSAGES,
  SERVICE_WORDS,
  passwordLength,
  passwordByteLength,
  evaluatePasswordRequirements,
  validatePassword,
  passwordRefusal,
  passwordPolicyFailures,
};
